import { Component, OnInit, ViewChild } from '@angular/core';
import { BaseListComponent } from '../../../../shared/base/base-list.component'
import { RouteAuthorities, ValueLabel } from 'src/app/shared/base/base.models';
import { AuthService } from '../../../../core/auth.service';
import { LoadingService } from '../../../../core/loading.service';
import { Dialog, DialogService } from '../../../../core/dialog.service';
import { AlertService } from '../../../../core/alert.service';
import { UtilityService } from '../../../../core/utility.service';
import { RequestService } from '../../../../core/request.service';
import { BaseService } from 'src/app/shared/base/base.service';
import { TranslateService } from '@ngx-translate/core';
import { Constants } from 'src/app/app.constants';
import { ProductCataloguesService } from '../product-catalogues.service';
import { CopyType, ProductCatalogue, ProductCatalogueStatus } from '../product-catalogues.models';
import { Product } from '../../products/products.models';
import { OptionsSource } from 'src/app/core/core.models';
import { ProductsService } from '../../products/products.service';
import { DxSelectBoxComponent } from 'devextreme-angular';
import { CompaniesService } from '../../companies/companies.service';
import { Company, CompanyType } from '../../companies/companies.models';
import { BusinessPartnersService } from '../../business-partners/business-partners.service';

@Component({
  selector: 'app-product-catalogues-list',
  templateUrl: './product-catalogues-list.component.html',
  styleUrls: ['./product-catalogues-list.component.scss']
})
export class ProductCataloguesListComponent extends BaseListComponent implements OnInit {
  @ViewChild('productsSelectBox', { static: true }) productsSelectBox: DxSelectBoxComponent;
  gridStateKey: string = 'product-catalogues_list_state_grid';
  lookupColumns: Array<string> = ['pointOfSale', 'code', 'partner.name', 'validFrom', 'validTo', 'status'];

  reportUrl: string = `${Constants.contextPath}/product-catalogues`;

  productCatalogueStatuses: ValueLabel[] = [];
  ProductCatalogueStatus = ProductCatalogueStatus;

  selectedCatalogueId: string;
  isCopyModalVisible: boolean = false;
  code: string;
  percentageFormat = '#%';

  selectedCopyType: string;
  copyTypes: ValueLabel[] = [];

  isSearchModalVisible: boolean = false;
  selectedProduct: Product;
  searchedProductCatalogues: ProductCatalogue[] = [];
  products: OptionsSource;

  today: Date = new Date();
  company: Company;
  CompanyType = CompanyType;
  businessPartners: OptionsSource;
  isEmailToPartnerModalVisible: boolean = false;
  selectedPartner: string;

  constructor(
    private productsService: ProductsService,
    private productCataloguesService: ProductCataloguesService,
    public authService: AuthService,
    public loadingService: LoadingService,
    public baseService: BaseService,
    public dialogService: DialogService,
    public alertService: AlertService,
    public utilityService: UtilityService,
    public requestService: RequestService,
    public translateService: TranslateService,
    private companiesService: CompaniesService,
    private businessPartnerService: BusinessPartnersService
  ) {
    super(authService, loadingService, baseService, dialogService, alertService, utilityService, requestService, translateService);
    this.authorities = new RouteAuthorities(authService, alertService, 'CD_CD', 'R_CD', 'U_CD', 'CD_CD');
    this.creatable = this.authorities.create;
    this.editable = this.authorities.update;
    this.deletable = this.authorities.delete;
    this.activatable = this.authorities.update;
  }

  async ngOnInit(): Promise<void> {
    this.company = await this.companiesService.getCompany(this.authService.companyId);
    this.url = `${Constants.contextPath}/simple-product-catalogues`;
    this.prepareLookupData();
    this.getGridData();
  }

  private prepareLookupData() {
    this.products = this.productsService.getProductsOptions()
    this.productCatalogueStatuses = this.utilityService.prepareLookupItems(this.utilityService.enumToSelectArray(ProductCatalogueStatus, true), 'value', 'label', 'ENUMS.PRODUCT_CATALOGUE_STATUS.');
    this.copyTypes = this.utilityService.prepareLookupItems(this.utilityService.enumToSelectArray(CopyType, true), 'value', 'label', 'ENUMS.COPY_TYPES.').filter(type => type.value != 'SELECTED');
    this.businessPartners = this.businessPartnerService.getBusinessPartnerOptions();
  }

  public loadGridState = (): any => {
    return this.loadGridStateBase(this.gridStateKey, this.lookupColumns);
  }

  public saveGridState = (state: any): void => {
    this.saveGridStateBase(state, this.gridStateKey, this.lookupColumns);
  }

  public openCopyModal(id: string) {
    this.selectedCatalogueId = id;
    this.isCopyModalVisible = true;
  }

  public closeCopyModal() {
    this.selectedCatalogueId = null;
    this.code = null;
    this.isCopyModalVisible = false;
  }

  public copyCatalogue() {
    if (this.code == null || this.code.length < 3) {
      this.alertService.error('PRODUCT_CATALOGUES.COPY_CODE_INVALID');
      return;
    }

    this.productCataloguesService.copyProductCatalogue(this.selectedCatalogueId, this.code, this.selectedCopyType, [], (response) => {
      this.getGridData(null, true)
      this.closeCopyModal();
      this.alertService.success('PRODUCT_CATALOGUES.CATALOGUE_COPIED_SUCCESSFULLY');
    }, (error) => {
      this.alertService.error(error, true);
    });
  }

  public changeCatalogueStatus(data: ProductCatalogue) {
    if (!this.checkValidDate(data.validTo))
      return;

    let activate: boolean = true;
    if (data.status == ProductCatalogueStatus[ProductCatalogueStatus.ACTIVE])
      activate = false;

    let dialogData = new Dialog(activate ? 'SHARED.ACTIVATE_QUESTION' : 'SHARED.DEACTIVATE_QUESTION', () => {
      this.productCataloguesService.changeStatus(<string>data.id, activate, (response) => {
        this.alertService.success(activate ? 'SHARED.ACTIVATE_SUCCESS' : 'SHARED.DEACTIVATE_SUCCESS');
        this.getGridData(null, true);
      });
    });

    this.dialogService.show(dialogData);
  }

  public changeCatalogueStatusFromModal(data: ProductCatalogue) {
    if (!this.checkValidDate(data.validTo))
      return;

    let activate: boolean = true;
    if (data.status == ProductCatalogueStatus[ProductCatalogueStatus.ACTIVE])
      activate = false;

    let dialogData = new Dialog(activate ? 'SHARED.ACTIVATE_QUESTION' : 'SHARED.DEACTIVATE_QUESTION', () => {
      this.productCataloguesService.changeStatus(<string>data.id, activate, (response) => {
        this.alertService.success(activate ? 'SHARED.ACTIVATE_SUCCESS' : 'SHARED.DEACTIVATE_SUCCESS');
        let index = this.searchedProductCatalogues.findIndex(p => p.id == data.id);
        if (index > -1) {
          if (activate) {
            this.searchedProductCatalogues[index].status = ProductCatalogueStatus[ProductCatalogueStatus.ACTIVE];
          } else {
            this.searchedProductCatalogues.splice(index, 1);
          }
        }
      });
    });

    this.dialogService.show(dialogData);
  }

  public checkValidDate(date: Date | string) {
    return new Date(date).toISOString() > new Date().toISOString();;
  }

  public openSearchModal() {
    this.isSearchModalVisible = true;
    setTimeout(() => {
      this.productsSelectBox.instance.focus();
      this.productsSelectBox.instance.open();
    }, 500);
  }

  public deactivateExpiredCatalogues() {
    let dialogData = new Dialog('PRODUCT_CATALOGUE.DEACTIVATE_EXPIRED_CATALOGUES_MESSAGE', () => {
      this.productCataloguesService.deactivateExpiredCatalogues((response) => {
        this.getGridData(null, true);
        this.alertService.success('PRODUCT_CATALOGUES.CATALOGUES_DEACTIVATED_SUCCESSFULLY');
      }, (error) => {
        this.alertService.error(error, true);
      });
    })
    this.dialogService.show(dialogData);
  }

  public closeSearchModal() {
    this.isSearchModalVisible = false;
  }

  public onProductChanged = (event) => {
    if (event && event.selectedItem)
      this.productCataloguesService.getProductCataloguesByProduct(event.selectedItem.code, (productCatalogues) => {
        this.searchedProductCatalogues = productCatalogues;
      });
  };

  public sendEmailToPartner() {
    this.productCataloguesService.sendEmailToPartner(this.selectedPartner, () => {
      this.isEmailToPartnerModalVisible = false;
      this.alertService.success('SHARED.EMAIL_SENT_SUCCESSFULLY');
      this.selectedPartner = null;
    });
  }

  public deleteCatalogue(id: string) {
    let dialog = new Dialog('SHARED.DELETE_QUESTION', () => {
      this.baseService.delete(`${Constants.contextPath}/product-catalogues`, id, () => {
        this.alertService.success('SHARED.DELETE_SUCCESS');
        this.getGridData(null, true);
      }, (error) => {
        this.alertService.error(error, true);
      });
    });

    this.dialogService.show(dialog);
  }
}
